import React, { useState } from 'react';
import {Button, DatePicker, Input, Space, Tooltip, message} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import {EyeIcon} from "@heroicons/react/24/outline";
import {Link} from "react-router-dom";
import {getDatesReport} from "../../services/reports/reportsService";
import * as XLSX from "xlsx";
import {RiFileExcel2Fill} from "react-icons/ri";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
} from "../../components/ui";

const { RangePicker } = DatePicker;

const FundsReport = () => {
    const { t } = useTranslation();
    const sum = (value) => `${value} ${t('common.currency')}`;
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const handleShowReport = async () => {
        setLoading(true);
        try {
            const start_at = dateRange[0] ? dateRange[0]?.format("YYYY-MM-DD") : '';
            const end_at = dateRange[1] ? dateRange[0]?.format("YYYY-MM-DD") : '';
            const apiResponse = await getDatesReport(start_at, end_at);

            if (apiResponse && apiResponse.data) {
                // Filter out items where count is zero
                const filteredData = apiResponse.data.filter(item => item.count !== 0);

                // Add a unique key to each item for the table
                const dataWithKey = filteredData.map((item, index) => ({ ...item, key: index + 1 }));
                setReportData(dataWithKey);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error('Ошибка при получении данных о пациентах:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleRangePickerChange = (dates) => {
        setDateRange(dates);
        // При изменении диапазона дат в RangePicker, даты сохраняются в state
    };


    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={t('reports.searchBy', { field: dataIndex })}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t('reports.search')}
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {t('reports.reset')}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
    });

    const renderHighlighted = (text, dataIndex) =>
        searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#1D2C7B', padding: 0, color: '#fff' }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : (
            text
        );

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const columns = [
        {
            title: t('patients.colId'),
            dataIndex: 'key',
            key: 'key',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            ...getColumnSearchProps('key'),
            render: (text) => (
                <span className="text-xs text-slate-500 tabular-nums">{renderHighlighted(text, 'key')}</span>
            ),
        },
        {
            title: t('common.date'),
            dataIndex: 'date_at',
            key: 'date_at',
            width: 150,
            sorter: (a, b) => a.date_at.localeCompare(b.date_at),
            ...getColumnSearchProps('date_at'),
            render: (text) => (
                <span className="text-sm font-medium text-[#1D2C7B] tabular-nums whitespace-nowrap">
                    {renderHighlighted(text, 'date_at')}
                </span>
            ),
        },
        {
            title: t('reports.patientsCount'),
            dataIndex: 'count',
            key: 'count',
            width: 200,
            align: 'right',
            sorter: (a, b) => a.count.localeCompare(b.count),
            ...getColumnSearchProps('count'),
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums">{renderHighlighted(text, 'count')}</span>
            ),
        },
        {
            title: t('reports.totalAmount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            sorter: (a, b) => new Date(a.amount) - new Date(b.amount),
            ...getColumnSearchProps('amount'),
            render: (text) => (
                <span className="text-sm font-medium text-slate-900 tabular-nums whitespace-nowrap">{sum(text)}</span>
            ),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 110,
            render: (text, record) => (
                <Tooltip title={t('reports.info')}>
                    <Link
                        to={`/reports/finance/${record.date_at}`}
                        className="inline-flex p-1.5 rounded-md text-slate-400 hover:text-[#1D2C7B] hover:bg-slate-100 transition-colors"
                    >
                        <EyeIcon className="w-[18px] h-[18px]"/>
                    </Link>
                </Tooltip>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-2">
                <Link
                    to={`/reports/finance/${record.date_at}`}
                    className="min-w-0 truncate text-sm font-semibold text-[#1D2C7B] tabular-nums"
                >
                    {record.date_at}
                </Link>
                <Tooltip title={t('reports.info')}>
                    <Link
                        to={`/reports/finance/${record.date_at}`}
                        className="inline-flex shrink-0 p-1.5 rounded-md text-slate-400 hover:text-[#1D2C7B] hover:bg-slate-100 transition-colors"
                    >
                        <EyeIcon className="w-[18px] h-[18px]"/>
                    </Link>
                </Tooltip>
            </div>

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('reports.patientsCount')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.count}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('reports.totalAmount')}</span>
                    <span className="block text-sm font-medium text-slate-900 tabular-nums">{sum(record.amount)}</span>
                </div>
            </div>
        </div>
    );

    const exportToExcel = () => {
        if (reportData.length === 0) {
            message.warning(t('reports.noExport'));
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(reportData.map(({ key, ...rest }) => rest)); // Убираем поле key
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Генерация файла Excel
        XLSX.writeFile(workbook, 'BemorReport.xlsx');
    };


    const handleEdit = (key) => {
        console.log(`Редактировать запись с ключом: ${key}`);
    };

    const handleDelete = (key) => {
        console.log(`Удалить запись с ключом: ${key}`);
    };

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('reports.finance')}
                    count={reportData.length}
                    filters={
                        <RangePicker
                            size="small"
                            className="w-full md:!w-[250px]"
                            onChange={handleRangePickerChange}
                            value={dateRange} // Значение должно быть dateRange, а не startDate и endDate
                        />
                    }
                    actions={
                        <>
                            <ActionButton
                                collapseOnMobile={false}
                                onClick={handleShowReport}
                                loading={loading}
                            >
                                {t('reports.show')}
                            </ActionButton>
                            <Button
                                type="default"
                                className="!h-9 !rounded-lg !inline-flex !items-center !gap-1.5"
                                icon={<RiFileExcel2Fill/>}
                                onClick={exportToExcel}
                                disabled={reportData.length === 0}
                            >
                                <span className="hidden sm:inline">{t('reports.exportExcel')}</span>
                            </Button>
                        </>
                    }
                />

                <DataTable
                    dataSource={reportData} // Use reportData instead of static data
                    columns={columns}
                    mobileCard={renderMobileCard}
                    scrollX={960}
                    loading={loading}
                    rowKey="key"
                    pagination={{
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                />
            </PageCard>
        </PageShell>
    );
};

export default FundsReport;
